/**
 * Centralized backend-error extraction + toast bridge.
 *
 * Single source of truth for how API failures surface to the user:
 *   1. `getBackendErrorMessage(err, fallback?)` digs through the many shapes the
 *      backend uses (`Message`/`message`/`error`/`title`/`errors[]`, raw string)
 *      and picks the most descriptive one.
 *   2. `notifyApiError(msg)` lets non-React code (axios interceptors) trigger
 *      a toast via a handler registered by `ToastProvider`.
 *
 * Opt-out per request: set `config.meta = { skipErrorToast: true }` when a
 * caller wants to render the error inline instead of via global toast.
 */

let NETWORK_ERROR_FALLBACK = 'No se pudo conectar con el servidor'
let GENERIC_FALLBACK = 'Ocurrió un error inesperado'

export function setApiErrorFallbacks(msgs: { network?: string; generic?: string }): void {
  if (msgs.network) NETWORK_ERROR_FALLBACK = msgs.network
  if (msgs.generic) GENERIC_FALLBACK = msgs.generic
}

type AnyRecord = Record<string, unknown>

// PascalCase compound words with no spaces are HTTP status names (e.g. "UnprocessableEntity",
// "BadRequest", "InternalServerError"). They come directly from the framework and are not
// user-friendly — discard them so the fallback is used instead.
const HTTP_STATUS_NAME_RE = /^(?:[A-Z][a-z]+){2,}$/

function pickString(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim() && !HTTP_STATUS_NAME_RE.test(c.trim())) return c.trim()
  }
  return undefined
}

/**
 * Extract the user-friendly error message from any thrown value.
 *
 * The backend always returns { Data, Message }. Priority:
 *   1. Network error (no response received) → fixed network fallback
 *   2. response.data.Message / .message     → backend's user-friendly string
 *   3. Response received but no Message     → fallback arg (generic)
 *   4. Non-axios error (e.g. authService wraps an extracted message in new Error()) → err.message
 */
export function getBackendErrorMessage(err: unknown, fallback: string = GENERIC_FALLBACK): string {
  if (!err) return fallback

  const anyErr   = err as AnyRecord
  const response = anyErr?.response as AnyRecord | undefined

  // No response at all → network-level failure (timeout, CORS, no connection).
  if (anyErr?.request && !response) return NETWORK_ERROR_FALLBACK

  if (response) {
    const data = response.data
    if (data && typeof data === 'object') {
      const msg = pickString((data as AnyRecord).Message, (data as AnyRecord).message)
      if (msg) return msg
    }
    // Response received but Message is absent or a status-code name → generic.
    return fallback
  }

  // Non-axios manually thrown errors (e.g. authService wraps the extracted message
  // in new Error() before re-throwing, so err.message already holds a user-facing string).
  if (!anyErr?.isAxiosError && err instanceof Error && err.message) return err.message

  return fallback
}

/**
 * Async variant for requests made with `responseType: 'blob'` (file
 * downloads — comprobantes, resúmenes, exports de movimientos).
 *
 * When a blob request fails, axios still returns the backend's `{ Message }`
 * body — but wrapped as a `Blob` instead of parsed JSON, because the whole
 * response was typed as blob. `getBackendErrorMessage` can't read a Blob's
 * content synchronously, so these failures were silently showing the generic
 * fallback instead of the backend's real message. This reads the Blob as
 * text, parses it as JSON when possible, and falls back to the sync path
 * (network errors, non-blob responses, etc.) otherwise.
 */
export async function resolveBackendErrorMessage(
  err: unknown,
  fallback: string = GENERIC_FALLBACK,
): Promise<string> {
  const anyErr   = err as AnyRecord
  const response = anyErr?.response as AnyRecord | undefined
  const data     = response?.data

  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as AnyRecord
      const msg = pickString(parsed.Message, parsed.message)
      if (msg) return msg
    } catch {
      // El blob de error no es JSON legible — cae al fallback genérico.
    }
    return fallback
  }

  return getBackendErrorMessage(err, fallback)
}

// ---------------------------------------------------------------------------
// Toast bridge — registered by <ToastProvider> at mount.
// ---------------------------------------------------------------------------

type ErrorNotifier = (message: string) => void

let _notifier: ErrorNotifier | null = null

export function setApiErrorHandler(fn: ErrorNotifier | null): void {
  _notifier = fn
}

export function notifyApiError(message: string): void {
  if (typeof window === 'undefined') return // SSR guard: no toast outside browser
  if (_notifier) _notifier(message)
}

// ---------------------------------------------------------------------------
// Request-level opt-out marker
// ---------------------------------------------------------------------------

/**
 * Read by the axios response interceptor. Attach to any request whose error UX
 * is handled by the caller (inline form message, custom modal, etc.).
 *
 * Example:
 *   api.get('/validate', { meta: { skipErrorToast: true } })
 *   api.post('/users/ChangePassword', body, { meta: { skipAuthRedirect: true } })
 */
export interface ApiRequestMeta {
  /** When true, the interceptor will NOT fire a toast for this request's error. */
  skipErrorToast?: boolean
  /**
   * When true, a 401 from this request will NOT redirect to /login.
   * Use for endpoints that legitimately return 401 for non-session reasons
   * (e.g. "wrong current password" on /users/ChangePassword).
   */
  skipAuthRedirect?: boolean
}

declare module 'axios' {
  interface AxiosRequestConfig {
    meta?: ApiRequestMeta
  }
  interface InternalAxiosRequestConfig {
    meta?: ApiRequestMeta
  }
}
